/**
 * Company-wide marketing statistics. Update here when figures refresh.
 *
 * Suffix convention (enforced by helpers — do not hand-roll at call sites):
 * - Compact currency amounts use lowercase Intl compact units: `b`, `m`, `k`
 *   (e.g. `$7b`, `£6b`, `€6b`) — never `bn` / `mn`.
 * - Business counts use `{n}k+` (e.g. `7k+` for 7,000 businesses).
 * - Helpers omit trailing `+`; append it in copy when the stat is a minimum
 *   (e.g. `${deployedAmount(locale)}+ deployed`).
 */
import { resolveSupportedLocale } from "@/i18n/routing";

import { convertedAmount } from "./converted-amount";

export const TOTAL_DEPLOYED_USD = 7_000_000_000;
export const TOTAL_BUSINESSES_FUNDED = 7000;
export const COMPANY_HEADCOUNT = 400;

/**
 * Trustpilot rating cited in marketing copy ("4.6 stars 500+ reviews").
 *
 * Deliberately static rather than fetched live: the score and review count are
 * hardcoded across heroes and landing pages, and a drifting live figure put one
 * page out of step with the rest. Refresh both values here when the rating moves.
 *
 * Kept numeric so ICU renders the locale's decimal separator (`4,6` in de/es/nl).
 */
export const TRUSTPILOT_SCORE = 4.6;
export const TRUSTPILOT_REVIEWS = 500;

/** J.P. Morgan warehouse line (2022). */
export const DEBT_FACILITY_USD = 300_000_000;
/** Series B equity raise (2022). */
export const SERIES_B_USD = 150_000_000;
/** Series A equity raise (2021). */
export const SERIES_A_USD = 76_000_000;

/**
 * Locale-converted total deployed capital (e.g. `$7b`, `£5b`, `€6b`, `$10b`).
 * Sources {@link TOTAL_DEPLOYED_USD} — a single constant edit refreshes every callsite.
 *
 * Rounds **down**: copy renders this as a minimum (`${deployedAmount(locale)}+`),
 * so £5.53b has to read "£5b+". Rounding to nearest gave "£6b+", which claims
 * more deployed capital than the figure supports. Use this rather than calling
 * `convertedAmount(TOTAL_DEPLOYED_USD, …)` directly, so every surface — metadata,
 * heroes, JSON-LD — agrees on one figure.
 */
export function deployedAmount(locale: string): string {
  return convertedAmount(
    TOTAL_DEPLOYED_USD,
    resolveSupportedLocale(locale),
    "down"
  );
}

/**
 * Compact business count for marketing copy (e.g. `7k+`).
 * Sources {@link TOTAL_BUSINESSES_FUNDED}.
 */
export function businessesFundedCompact(_locale: string): string {
  return `${Math.round(TOTAL_BUSINESSES_FUNDED / 1000)}k+`;
}

/** Locale-formatted business count, e.g. "7,000+" (en) or "7.000+" (de). */
export function businessesFundedFormatted(locale: string): string {
  return `${TOTAL_BUSINESSES_FUNDED.toLocaleString(resolveSupportedLocale(locale))}+`;
}
